import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { type ClientPlatform, installCopy } from '../core/install/install';
import { CtButton } from './button.directive';

/**
 * Bandeau d'installation, sous l'en-tête.
 *
 * Présentation seulement : qui l'affiche (téléphone, onglet, pas encore
 * installé) est décidé par `InstallPromptService`. iOS n'a pas d'API
 * d'installation — on décrit le geste Partager. Android, si Chrome a levé
 * `beforeinstallprompt`, a un vrai bouton.
 */
@Component({
  selector: 'ct-install-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CtButton],
  template: `
    <aside class="banner" role="region" [attr.aria-label]="copy().title">
      <div class="copy">
        <p class="title">{{ copy().title }}</p>
        <p class="body">{{ copy().body }}</p>
        @if (copy().iosHowTo; as howTo) {
          <p class="how">
            <span class="glyph" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="16" height="16">
                <path
                  fill="currentColor"
                  d="M10 2.2 6.8 5.4a.75.75 0 1 0 1.06 1.06L9.25 5.07V12a.75.75 0 0 0 1.5 0V5.07l1.39 1.39a.75.75 0 1 0 1.06-1.06L10 2.2ZM5.5 9.5A.75.75 0 0 0 4 9.5V15A2.5 2.5 0 0 0 6.5 17.5h7A2.5 2.5 0 0 0 16 15V9.5a.75.75 0 0 0-1.5 0V15a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V9.5Z"
                />
              </svg>
            </span>
            {{ howTo }}
          </p>
        }
        @if (copy().androidHowTo; as howTo) {
          <p class="how">{{ howTo }}</p>
        }
      </div>
      <div class="actions">
        @if (copy().nativeAction; as label) {
          <button type="button" ctButton="primary" (click)="install.emit()">{{ label }}</button>
        }
        <button type="button" class="dismiss" aria-label="Fermer" (click)="dismiss.emit()">
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </aside>
  `,
  styles: `
    :host {
      display: block;
      background: var(--ct-kraft-dark);
      border-bottom: 1px solid var(--ct-border);
    }

    .banner {
      width: 100%;
      max-width: 60rem;
      margin: 0 auto;
      padding: var(--ct-space-2) var(--ct-space-4);
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: var(--ct-space-3);
      align-items: start;
    }

    .copy {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .title {
      margin: 0;
      font-size: var(--ct-size-sm);
      font-weight: var(--ct-weight-bold);
      color: var(--ct-vert-cageot);
    }

    .body,
    .how {
      margin: 0;
      font-size: var(--ct-size-xs);
      color: var(--ct-text);
    }

    .how {
      display: flex;
      align-items: flex-start;
      gap: var(--ct-space-1);
    }

    .glyph {
      display: inline-flex;
      color: var(--ct-vert-cageot);
      flex-shrink: 0;
      margin-top: 1px;
    }

    .actions {
      display: flex;
      align-items: center;
      gap: var(--ct-space-2);
    }

    .actions .ct-btn {
      padding: var(--ct-space-2) var(--ct-space-3);
      font-size: var(--ct-size-sm);
      white-space: nowrap;
    }

    .dismiss {
      display: grid;
      place-items: center;
      width: 2rem;
      height: 2rem;
      padding: 0;
      border: 0;
      background: transparent;
      color: var(--ct-text-muted);
      font-size: 1.35rem;
      line-height: 1;
      cursor: pointer;
    }
  `,
})
export class CtInstallBanner {
  readonly platform = input<ClientPlatform>('other');
  readonly nativeInstall = input(false);
  readonly install = output<void>();
  readonly dismiss = output<void>();

  protected readonly copy = computed(() => installCopy(this.platform(), this.nativeInstall()));
}
